import { useMutation, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import { settingSupport } from 'services/setting'
import posthog from 'posthog-js'

export type TSupportErrors = Partial<Record<keyof ISupportSetting, string>>

/**
 * The server normalises what the manager typed (`@x`, `ble.ir/x`, `+98…`) and
 * refuses what it cannot read with a message per field — those come back
 * through `onFieldErrors` so they sit under the field they belong to.
 */
export default function useSetFundSupport(
    refetch: () => void,
    onFieldErrors: (errors: TSupportErrors) => void
) {
    const queryClient = useQueryClient()
    const { isLoading, mutate } = useMutation({
        mutationFn: (payload: ISettingSupportPayload) => settingSupport(payload),
        onSuccess: (data, variables) => {
            const saved: ISupportSetting | undefined = data?.support_setting
            posthog.capture('fund_support_settings_saved', {
                fund_id: variables.moneyPool,
                has_phone: !!(saved ?? variables).support_phone,
                has_bale: !!(saved ?? variables).support_bale,
                has_eitaa: !!(saved ?? variables).support_eitaa,
            })
            onFieldErrors({})
            refetch()
            // the fund page reads the same fields as its «پشتیبانی صندوق» card
            queryClient.invalidateQueries([
                'moneyPool-management',
                variables.moneyPool,
            ])
            toast.success('تغییرات با موفقیت انجام شد')
        },
        onError: (error: any, variables) => {
            const status = error?.response?.status
            posthog.capture('fund_setting_save_failed', {
                section: 'support',
                fund_id: variables?.moneyPool,
                status,
            })
            const body = error?.response?.data
            const fields: TSupportErrors = {}
            if (status === 400 && body && typeof body === 'object') {
                ;(
                    ['support_phone', 'support_bale', 'support_eitaa'] as const
                ).forEach((key) => {
                    const message = body[key]
                    if (message)
                        fields[key] = Array.isArray(message)
                            ? String(message[0])
                            : String(message)
                })
            }
            onFieldErrors(fields)
            toast.error(
                Object.keys(fields).length
                    ? 'لطفاً خطای فیلدها را برطرف کنید.'
                    : 'مشکلی پیش آمده!'
            )
        },
    })

    return { mutate, isLoading }
}
